import React, { useState } from 'react';
import classNames from 'classnames';
import styles from '../assets/order-grid.module.scss';
import { Paginated } from 'src/services/api-handlers/pagination';
import Grid, { Column } from 'src/components/react-table/Grid';
import textFilter from 'src/components/react-table/filters/text-filter';
import { QueryParams } from 'src/types/grid';
import { IOrder } from 'src/types/order';
import OrderActionsColumn from './OrderActionsColumn';
import { CellProps } from 'react-table';
import OrderEnum from 'src/enumerables/order-enum';
import { getValFromEnum } from 'src/components/redux-form/utils/dropdown-values';
import dateFilter from 'src/components/react-table/filters/date-filter';
import dropDownFilter from 'src/components/react-table/filters/dropdown-filter';
import { Link } from 'react-router-dom';
import Router from 'src/navigation/router';
import { routes } from 'src/navigation';
import { changeToZeroMoment } from '../../../../../../../helpers/timeHelpers';
import moment from 'moment';
import dateFilterMulti from '../../../../../../../components/react-table/filters/date-filter-multi';
import dropdownOrderFilter from '../../../../../../../components/react-table/filters/dropdown-order-filter';
import dropdownFilter from 'src/components/react-table/filters/dropdown-filter';

interface Props {
    data: Paginated<IOrder>;
    getData: (params: QueryParams) => void;
    serviceTypes: SelectObject[];
}

const typeEnum = OrderEnum ? OrderEnum.getEnum('OrderType') : {};
const orderTypes: SelectObject[] = getValFromEnum(typeEnum);

const typeEnumCaseType = OrderEnum ? OrderEnum.getEnum('OrderCaseType') : {};
const orderCaseTypes: SelectObject[] = getValFromEnum(typeEnumCaseType);

const OrderStatusValues = OrderEnum ? OrderEnum.getEnum('OrderStatus') : {};
const valuesOrderStatus: SelectObject[] = getValFromEnum(OrderStatusValues);

const ServiceTypeProviderValues = OrderEnum ? OrderEnum.getEnum('ServiceTypeProvider') : {};
const valuesServiceTypeProvider: SelectObject[] = getValFromEnum(ServiceTypeProviderValues);

const getColumns = (serviceTypes: SelectObject[]): Column<IOrder>[] => [
    {
        Header: '#',
        accessor: 'id',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => (
            <Link to={Router.generate(routes.ORDER_DETAIL, { id: original.id })}>{original.id}</Link>
        ),
        disableSortBy: false,
        disableFilters: true,
        Filter: textFilter<IOrder>(),
    },
    {
        Header: 'Name',
        accessor: 'name',

        Filter: textFilter<IOrder>(),
    },
    {
        Header: 'Case #',
        accessor: 'caseNumber',
        disableSortBy: true,
        Filter: textFilter<IOrder>(),
    },
    {
        Header: 'Order Case',
        accessor: 'orderCaseType',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const valueOrderType = orderCaseTypes.find((it) => it.value === original.orderCaseType);
            return valueOrderType ? valueOrderType.label : '';
        },
        Filter: dropDownFilter<IOrder>(orderCaseTypes),
    },

    {
        Header: 'Order Type',
        accessor: 'orderType',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const valueOrderType = orderTypes.find((it) => it.value === original.orderType);
            return valueOrderType ? valueOrderType.label : '';
        },
        disableSortBy: true,
        Filter: dropDownFilter<IOrder>(orderTypes),
    },
    {
        Header: 'Student',
        disableSortBy: true,
        accessor: 'student.lastName',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) =>
            original.student ? `${original.student.firstName} ${original.student.lastName}` : null,
        Filter: textFilter<IOrder>('text', 500, 'Search by Last name'),
    },
    {
        Header: 'Service',
        disableSortBy: true,
        accessor: 'serviceType.id',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const labelArr = [];
            if (original?.serviceType?.title) {
                labelArr.push(original.serviceType.title);
            }
            if (original?.serviceType?.cpt) {
                labelArr.push(`CPT: [${original.serviceType.cpt}]`);
            }
            const label = labelArr.join(', ');
            return label;
        },

        //disableFilters: true,
        Filter: dropDownFilter<IOrder>(serviceTypes, styles.minWidth),
    },
    {
        Header: 'Validity from',
        accessor: 'caseNumberValidityFrom[after]',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const date = original.caseNumberValidityFrom ? original.caseNumberValidityFrom.split('T')[0] : '';
            return changeToZeroMoment(date, 'MMM D, YYYY');
        },
        disableSortBy: true,
        Filter: dateFilter<IOrder>(),
    },
    {
        Header: 'Order Expiration',
        accessor: 'orderExpiration[after]',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const date = original.orderExpiration ? original.orderExpiration.split('T')[0] : '';
            return date ? changeToZeroMoment(date, 'MMM D, YYYY') : '';
        },
        disableSortBy: true,
        Filter: dateFilter<IOrder>(),
    },

    {
        Header: 'Validity to',
        accessor: 'caseNumberValidityTo[after]',
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const date = original.caseNumberValidityTo ? original.caseNumberValidityTo.split('T')[0] : '';
            return changeToZeroMoment(date, 'MMM D, YYYY');
        },
        disableSortBy: true,
        Filter: dateFilter<IOrder>(),
    },

    {
        Header: 'Status',
        accessor: 'status',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const valueStatus = valuesOrderStatus.find((it) => it.value === original.status);
            return valueStatus ? valueStatus.label : null;
        },
        Filter: dropdownOrderFilter<IOrder>(valuesOrderStatus),
    },
    {
        Header: 'Service Type Provider',
        accessor: 'serviceTypeProvider',
        disableSortBy: true,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            const valueServiceTypeProvider = valuesServiceTypeProvider.find(
                (it) => it.value === original.serviceTypeProvider,
            );
            return valueServiceTypeProvider ? valueServiceTypeProvider.label : null;
        },
        Filter: dropdownOrderFilter<IOrder>(valuesServiceTypeProvider),
    },
    {
        Header: 'Created',
        accessor: 'createdAt',
        disableSortBy: false,
        Cell: ({
            cell: {
                row: { original },
            },
        }: CellProps<IOrder>) => {
            return (
                <div className={'text-center'}>
                    {original.createdAt ? moment(original.createdAt).format('MMM D, YYYY') : ''}
                </div>
            );
        },
        Filter: dateFilterMulti<IOrder>(),
    },
    {
        Header: 'Actions',
        accessor: 'updated',
        Cell: OrderActionsColumn,
        disableSortBy: true,
        disableFilters: true,
        width: '10%',
    },
];

const OrdersGrid: React.FunctionComponent<Props> = ({ data, getData, serviceTypes }: Props) => {
    const [columns, setColumns] = useState<any>([]);

    if (serviceTypes.length && !columns.length) {
        setColumns(getColumns(serviceTypes));
    }

    return (
        <div className={classNames(styles.grid)}>
            <Grid<IOrder> columns={columns} data={data} getData={getData} />
        </div>
    );
};

export default OrdersGrid;
